/**
 * Safe localStorage wrapper with error handling and fallback to in-memory storage
 * Prevents crashes in private browsing mode or when storage quota is exceeded
 */

class SafeStorage {
    private memoryStorage: Map<string, string> = new Map();
    private isLocalStorageAvailable: boolean = true;

    constructor() {
        this.checkAvailability();
    }

    /**
     * Check if localStorage is available
     */
    private checkAvailability(): void {
        try {
            const testKey = '__storage_test__';
            localStorage.setItem(testKey, 'test');
            localStorage.removeItem(testKey);
            this.isLocalStorageAvailable = true;
        } catch {
            this.isLocalStorageAvailable = false;
            console.warn('[SafeStorage] localStorage unavailable, using in-memory fallback');
        }
    }

    /**
     * Safely get an item from storage
     * @param key Storage key
     * @returns Value or null if not found/error
     */
    getItem(key: string): string | null {
        try {
            if (this.isLocalStorageAvailable) {
                return localStorage.getItem(key);
            }
            return this.memoryStorage.get(key) ?? null;
        } catch (error) {
            console.warn(`[SafeStorage] Failed to get item "${key}":`, error);
            return this.memoryStorage.get(key) ?? null;
        }
    }

    /**
     * Safely set an item in storage
     * @param key Storage key
     * @param value Value to store
     * @returns true if successful, false otherwise
     */
    setItem(key: string, value: string): boolean {
        try {
            if (this.isLocalStorageAvailable) {
                localStorage.setItem(key, value);
                // Also store in memory as backup
                this.memoryStorage.set(key, value);
                return true;
            }
            this.memoryStorage.set(key, value);
            return true;
        } catch (error) {
            console.warn(`[SafeStorage] Failed to set item "${key}":`, error);
            // Fallback to memory storage
            try {
                this.memoryStorage.set(key, value);
                // Mark localStorage as unavailable if quota exceeded
                if (error instanceof Error && error.name === 'QuotaExceededError') {
                    this.isLocalStorageAvailable = false;
                }
                return true;
            } catch {
                return false;
            }
        }
    }

    /**
     * Safely remove an item from storage
     * @param key Storage key
     * @returns true if successful, false otherwise
     */
    removeItem(key: string): boolean {
        try {
            if (this.isLocalStorageAvailable) {
                localStorage.removeItem(key);
            }
            this.memoryStorage.delete(key);
            return true;
        } catch (error) {
            console.warn(`[SafeStorage] Failed to remove item "${key}":`, error);
            this.memoryStorage.delete(key);
            return false;
        }
    }

    /**
     * Safely clear all items from storage
     * @returns true if successful, false otherwise
     */
    clear(): boolean {
        try {
            if (this.isLocalStorageAvailable) {
                localStorage.clear();
            }
            this.memoryStorage.clear();
            return true;
        } catch (error) {
            console.warn('[SafeStorage] Failed to clear storage:', error);
            this.memoryStorage.clear();
            return false;
        }
    }

    /**
     * Get all keys in storage
     * @returns Array of keys
     */
    keys(): string[] {
        try {
            if (this.isLocalStorageAvailable) {
                return Object.keys(localStorage);
            }
            return Array.from(this.memoryStorage.keys());
        } catch (error) {
            console.warn('[SafeStorage] Failed to get keys:', error);
            return Array.from(this.memoryStorage.keys());
        }
    }

    /**
     * Check if a key exists in storage
     * @param key Storage key
     * @returns true if key exists
     */
    hasItem(key: string): boolean {
        return this.getItem(key) !== null;
    }

    /**
     * Get item and parse as JSON
     * @param key Storage key
     * @returns Parsed object or null
     */
    getJSON<T = unknown>(key: string): T | null {
        try {
            const value = this.getItem(key);
            if (value === null) return null;
            return JSON.parse(value) as T;
        } catch (error) {
            console.warn(`[SafeStorage] Failed to parse JSON for "${key}":`, error);
            return null;
        }
    }

    /**
     * Stringify and store object as JSON
     * @param key Storage key
     * @param value Object to store
     * @returns true if successful
     */
    setJSON(key: string, value: unknown): boolean {
        try {
            const jsonString = JSON.stringify(value);
            return this.setItem(key, jsonString);
        } catch (error) {
            console.warn(`[SafeStorage] Failed to stringify JSON for "${key}":`, error);
            return false;
        }
    }

    /**
     * Get storage status
     */
    getStatus(): { available: boolean; usingMemory: boolean } {
        return {
            available: this.isLocalStorageAvailable,
            usingMemory: !this.isLocalStorageAvailable || this.memoryStorage.size > 0
        };
    }
}

// Export singleton instance
export const safeStorage = new SafeStorage();
